/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router';
import { connect } from 'react-redux';
import toNumber from 'lodash/toNumber';
import throttle from 'lodash/throttle';

import {
    Icon,
    ImageUnavailable,
    InventoryTag,
    Button,
    StringHelper,
    Heading,
    Tooltip,
} from 'sarsaparilla';

import * as MapHelper from 'shared-ui/maps/helpers/mapHelper';

import StarDisplay from 'ui-rating-review/src/components/StarDisplay';
import { formatLocationName } from '../utils/stringUtils';
import { getItemDescription, kmsToMiles } from '../utils/tripUtils';
import * as tripPlannerActions from '../actions/tripPlanner';

const propTypes = {
    dispatch: PropTypes.func.isRequired,
    editable: PropTypes.bool,
    destination: PropTypes.object,
    destinationIndex: PropTypes.number,
    item: PropTypes.object,
    onCardEnter: PropTypes.func,
};

class TripSearchResultCard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            windowWidth: 0,
            cardHeight: null,
        };

        this.handleWindowResize = throttle(this.handleWindowResize.bind(this), 500);
        this.onCardEnter = this.onCardEnter.bind(this);
        this.addToItinerary = this.addToItinerary.bind(this);
        this.onDeleteTripItem = this.onDeleteTripItem.bind(this);
        this.searchResultsCard = React.createRef();
    }

    componentDidMount() {
        this.handleWindowResize();
        window.addEventListener('resize', this.handleWindowResize);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize);
    }

    onCardEnter(item) {
        if (this.props.onCardEnter) {
            this.props.onCardEnter(item);
        }
    }

    onDeleteTripItem() {
        const i = this.props.item;
        const item = {
            entity_id: i.entity_id,
            entity_type: i.entity_type,
            lat: i.latitude,
            lng: i.longitude,
            name: i.name,
            reservable: i.reservable,
            availability: i.availability,
        };

        this.props.dispatch(
            tripPlannerActions.deleteItem(this.props.destinationIndex, item)
        );
        this.props.dispatch(
            tripPlannerActions.setActiveDestinationIndex(
                this.props.destinationIndex,
                true,
                false,
                true
            )
        );
    }

    getTargetUri(item) {
        if (item.entity_id) {
            return MapHelper.getTargetUri(item);
        }
        return null;
    }

    getPriceRangeLabel(item) {
        if (item && item.price_range) {
            const range = item.price_range;

            if (range) {
                const minAmount = range.amount_min;
                const maxAmount = range.amount_max;

                if (minAmount > -1) {
                    if (minAmount === maxAmount) {
                        return `$${Math.floor(minAmount)}`;
                    }

                    return `$${Math.floor(minAmount)} - $${Math.floor(maxAmount)}`;
                }
            }
        }
        return null;
    }

    getPriceRangeUnit(item) {
        if (item && item.price_range) {
            const range = item.price_range;

            if (range) {
                return ` / ${range.per_unit}`;
            }
        }
        return null;
    }

    handleWindowResize() {
        const windowWidth = window.innerWidth;
        if (this.state.windowWidth !== windowWidth) {
            this.setState({
                windowWidth,
                cardHeight: this.searchResultsCard.offsetHeight,
            });
        }
    }

    addToItinerary() {
        const i = this.props.item;
        const item = {
            entity_id: i.entity_id,
            entity_type: i.entity_type,
            lat: toNumber(i.latitude),
            lng: toNumber(i.longitude),
            name: i.name,
            reservable: i.reservable,
            availability: i.availability,
            availability_counts: i.availability_counts,
            org_id: i.org_id,
            org_name: i.org_name,
            parent_id: i.parent_id,
            parent_name: i.parent_name,
            parent_type_name: i.parent_type_name,
            campsites_count: i.campsites_count,
            tours_count: i.tours_count,
            campsite_type_of_use: i.campsite_type_of_use,
            activities: i.activities,
            city: i.city,
            state_code: i.state_code,
            country_code: i.country_code,
            description: i.description,
            type: i.type,
        };

        this.props.dispatch(
            tripPlannerActions.addItem(this.props.destinationIndex, item)
        );
        this.props.dispatch(
            tripPlannerActions.setActiveDestinationIndex(
                this.props.destinationIndex,
                true,
                false,
                true
            )
        );
    }

    isItemSelected() {
        const items = this.props.destination.items;

        for (let i = 0; i < items.length; i += 1) {
            if (
                this.props.item.entity_id === items[i].entity_id &&
                this.props.item.entity_type === items[i].entity_type
            ) {
                return true;
            }
        }
        return false;
    }

    renderItemType(item) {
        if (item.entity_type === 'recarea') {
            return <InventoryTag.InventoryRecArea hasText />;
        }
        if (item.entity_type === 'permit') {
            return <InventoryTag.InventoryPermit hasText />;
        }
        if (item.entity_type === 'vehiclepermit') {
            return <InventoryTag.InventoryPermit hasText />;
        }
        if (item.entity_type === 'campground') {
            const pills = [];
            if (
                item.campsite_type_of_use &&
                item.campsite_type_of_use.length > 0 &&
                item.campsite_type_of_use[0] !== 'na'
            ) {
                // calculate pills for campgrounds, day use, and mixed types
                item.campsite_type_of_use.forEach((type) => {
                    if (type === 'Day') {
                        pills[pills.length] = (
                            <InventoryTag.InventoryDayUse hasText key={pills.length} />
                        );
                    } else if (type === 'Overnight') {
                        pills[pills.length] = (
                            <InventoryTag.InventoryCamping hasText key={pills.length} />
                        );
                    }
                });
            } else {
                // in case of blank type use, assume a campground
                pills[pills.length] = (
                    <InventoryTag.InventoryCamping key={pills.length} />
                );
            }

            return pills;
        }
        if (item.entity_type === 'ticketfacility') {
            return <InventoryTag.InventoryTicket hasText />;
        }
        if (item.entity_type === 'tour') {
            return <InventoryTag.InventoryTicket hasText />;
        }
        if (item.entity_type === 'timedentry') {
            return <InventoryTag.InventoryTicket hasText />;
        }
        if (item.entity_type === 'timedentry_tour') {
            return <InventoryTag.InventoryTicket hasText />;
        }
        if (item.entity_type === 'activitypass') {
            return <InventoryTag.InventoryActivityPass hasText />;
        }
        if (item.entity_type === 'treepermit') {
            return <InventoryTag.InventoryTreePermits hasText />;
        }
        return <InventoryTag.InventoryPOI hasText />;
    }

    renderAlertNotice(item) {
        if (item && item.notices) {
            let alertNotice = null;
            for (let i = 0; i < item.notices.length; i += 1) {
                const notice = item.notices[i];
                if (notice.type === 'alert') {
                    alertNotice = notice;
                    break;
                }
            }
            if (alertNotice) {
                const detailURL = this.getTargetUri(item);
                return (
                    <Link to={detailURL} aria-label={alertNotice.text}>
                        <Icon iconName="warning" />
                    </Link>
                );
            }
        }
        return null;
    }

    renderParentOrg(item) {
        const parentURL = this.getTargetUri({
            entity_id: item.parent_id,
            entity_type: item.parent_type,
        });
        if (item.parent_name && item.parent_name.length > 0 && parentURL) {
            return <li>{formatLocationName(item.parent_name)}</li>;
        }
        if (item.parent_name && item.parent_name.length > 0) {
            return <li>{formatLocationName(item.parent_name)}</li>;
        }
        if (item.org_name && item.org_name.length > 0) {
            return <li>{formatLocationName(item.org_name)}</li>;
        }
        return null;
    }

    renderAvailability(item) {
        if (item.type === 'CAMPING_LOTTERY') {
            return <div className="rec-flex-card-lottery-info">Lottery</div>;
        }
        if (item.availability && item.entity_type === 'campground') {
            if (item.availability === 'partially available') {
                return (
                    <div className="rec-flex-card-available-info">
                        Partially Available
                    </div>
                );
            }
            if (item.availability === 'available') {
                return <div className="rec-flex-card-available-info">Available</div>;
            }
            if (item.availability === 'not reservable') {
                return (
                    <div className="rec-flex-card-firstcome-info">
                        First-come, First-served
                    </div>
                );
            }
            return (
                <div className="rec-flex-card-unavailable-info">
                    {StringHelper.toTitleCase(item.availability)}
                </div>
            );
        }
        return null;
    }

    renderLayout(item) {
        const detailURL = this.getTargetUri(item);
        const priceStr = this.getPriceRangeLabel(item);
        const priceUnit = this.getPriceRangeUnit(item);
        const isSelected = this.isItemSelected();

        const isLargeViewport = this.state.windowWidth >= 1240;
        const setHeightOfImage = this.state.cardHeight;

        // Generate Campsite link
        //let campsitesCountLink = null;
        //if (item.reservable && (item.campsites_count || item.availability_counts)){
        //    const count = item.campsites_count;
        //    let availCount = 0;
        //    if (item.availability_counts &&
        //        item.availability_counts.Available &&
        //        item.availability_counts.Available > 0) {
        //        availCount = item.availability_counts.Available
        //    }
        //    let countStr = `${count}`;
        //    let siteStr = count > 1 ? "total sites" : "total site";
        //    if  (item.campsite_reserve_type && includes(item.campsite_reserve_type, 'Non Site-Specific')) {
        //        countStr += '+';
        //        if (siteStr.endsWith("Site")) {
        //            siteStr += "s";
        //        }
        //    }
        //    if (availCount>0){
        //        // commenting out for now available site count due to campsite filtering issues
        //        //countStr = `${availCount}`;
        //       //siteStr = availCount > 1 ? "Available Sites" : "Available Site";
        //    }
        //    campsitesCountLink = (
        //        <li>
        //            <Link
        //                target="_blank"
        //                to={`${detailURL}/campsites`}
        //                className="search-campground-sites">
        //                <span className="rec-sr-only">{formatLocationName(item.name)}</span>
        //                {countStr} {siteStr}
        //            </Link>
        //        </li>
        //    )
        //}

        return (
            <div
                className="rec-flex-card-wrap"
                data-testid="trip-planner-search-result-card"
                onMouseEnter={this.onCardEnter.bind(this, item)}
            >
                <a
                    className="rec-flex-card-image-wrap"
                    style={{ height: isLargeViewport ? setHeightOfImage : '' }}
                    href={detailURL}
                    id={`preview-image-${item.entity_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`View ${item.name} in new tab`}
                >
                    {this.props.item.preview_image_url && (
                        <img
                            src={this.props.item.preview_image_url}
                            alt={item.name}
                            className="search-card-image"
                        />
                    )}
                    {!this.props.item.preview_image_url && <ImageUnavailable />}

                    {this.renderAvailability(item)}
                </a>

                <div
                    className="rec-flex-card-content-wrap"
                    ref={(elem) => {
                        this.searchResultsCard = elem;
                    }}
                >
                    <a
                        href={detailURL}
                        id={`preview-image-${item.entity_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View ${item.name} in new tab`}
                    >
                        <div className="rec-flex-card-content-top-wrap">
                            <div className="rec-flex-card-top-left-content-wrap">
                                <div className="rec-flex-card-price-pill-wrap">
                                    <ul className="rec-flex-card-pill-wrap">
                                        {this.renderItemType(item)}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="rec-flex-card-body-wrap">
                            <Heading
                                headingLevel={4}
                                appearance="h5"
                                className="rec-flex-card-title"
                            >
                                {formatLocationName(item.name)}
                            </Heading>

                            {this.renderAlertNotice(item)}

                            <ul className="rec-flex-card-meta">
                                {this.renderParentOrg(item)}
                                {item.city && item.state_code && (
                                    <li>
                                        Near {item.city}, {item.state_code}
                                    </li>
                                )}
                                {item.distance && item.distance.length > 0 && (
                                    <li>
                                        {item.distance < 1 && (
                                            <>
                                                {kmsToMiles(parseFloat(item.distance))}
                                                {' miles away'}
                                            </>
                                        )}
                                        {item.distance >= 1 && (
                                            <Tooltip content="Point-to-point distance - also known as 'as the crow flies' - describes the straight-line distance from one location to another. It may be different from driving distance. Please verify actual distance based on your method of travel.">
                                                <Button
                                                    appearance="subtle"
                                                    size="sm"
                                                    iconAfterElement={
                                                        <Icon iconName="info" />
                                                    }
                                                    className={
                                                        'search-distance-info-button'
                                                    }
                                                    screenReaderTextBefore="Show accessible campsite filter help text"
                                                >
                                                    {kmsToMiles(
                                                        parseFloat(item.distance)
                                                    )}
                                                    {' miles point-to-point'}
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </li>
                                )}
                            </ul>
                        </div>

                        <p className="rec-flex-card-description">
                            {getItemDescription(item)}
                        </p>
                    </a>

                    <div className="rec-flex-card-content-bottom-wrap">
                        {item.number_of_ratings && item.number_of_ratings > 0 && (
                            <div className="rating-info">
                                <StarDisplay value={item.average_rating} />(
                                {item.number_of_ratings})
                            </div>
                        )}

                        <div className="rec-flex-card-price-button-wrap">
                            {item.entity_type === 'campground' && item.reservable && (
                                <div className="rec-flex-card-price-wrap">
                                    {priceStr && (
                                        <span className="rec-flex-card-price">
                                            {priceStr}
                                        </span>
                                    )}
                                    {priceUnit && (
                                        <span className="rec-flex-card-duration">
                                            {priceUnit}
                                        </span>
                                    )}
                                </div>
                            )}

                            {this.props.editable && isSelected && (
                                <Button
                                    size="xs"
                                    appearance="tertiary"
                                    onClick={this.onDeleteTripItem}
                                    aria-label={`Remove ${formatLocationName(
                                        item.name
                                    )} from Itinerary`}
                                >
                                    Remove from Itinerary
                                </Button>
                            )}

                            {this.props.editable && !isSelected && (
                                <Button
                                    size="xs"
                                    onClick={this.addToItinerary}
                                    aria-label={`Add ${formatLocationName(
                                        item.name
                                    )} to Itinerary`}
                                >
                                    Add to Itinerary
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const item = this.props.item;
        if (item !== undefined && item !== null) {
            return this.renderLayout(item);
        }
        return null;
    }
}

TripSearchResultCard.propTypes = propTypes;

function select() {
    return {};
}

export default connect(select)(TripSearchResultCard);
